import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useSettings, resolveImageUrl } from "../../lib/SettingsContext";

export default function Hero({ onQuoteClick }) {
  const { settings } = useSettings();
  const heroImg = resolveImageUrl(settings?.hero_image_url) ||
    "https://images.unsplash.com/photo-1570978541623-fe3fbe775696?crop=entropy&cs=srgb&fm=jpg&q=85&w=900";
  return (
    <section id="home" className="relative pt-32 lg:pt-40 pb-4 lg:pb-6 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-white via-white to-brand-mist" />
        <div className="absolute -top-32 -right-20 h-[480px] w-[480px] rounded-full bg-brand-blue/10 blur-3xl" />
        <div className="absolute -bottom-40 -left-32 h-[460px] w-[460px] rounded-full bg-brand-green/10 blur-3xl" />
        <div className="absolute inset-0 grain opacity-60" />
      </div>

      <div className="container-x grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white/80 backdrop-blur px-4 py-1.5 text-xs font-bold uppercase tracking-[0.18em] text-brand-blue"
          >
            <Sparkles className="h-3.5 w-3.5 text-brand-green" />
            Bhopal · Premium Print Studio
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight"
          >
            Premium <span className="relative inline-block">
              <span className="relative z-10 text-brand-blue">Printing</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-brand-green/30 -z-0 rounded" />
            </span> <span className="text-brand-green">+</span> Daily Services <br className="hidden sm:block" />for Your Business.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-base sm:text-lg text-gray-600 max-w-xl leading-relaxed"
          >
            Flex, Banner, Acrylic, Photo Framing, All printing work + trusted Daily Help Services. One reliable partner for everything your business and home need in Bhopal.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <button data-testid="hero-quote-btn" onClick={onQuoteClick} className="btn-primary">
              Get a Quote <ArrowRight className="h-4 w-4" />
            </button>
            <a href="#services" data-testid="hero-services-btn" className="btn-outline">
              Our Services
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-12 grid grid-cols-3 max-w-md gap-6"
          >
            {[
              { k: "10+", v: "Years Crafted" },
              { k: "5K+", v: "Projects Done" },
              { k: "98%", v: "Repeat Clients" },
            ].map((s) => (
              <div key={s.v}>
                <div className="font-display text-3xl font-extrabold text-brand-ink">{s.k}</div>
                <div className="text-xs uppercase tracking-[0.16em] text-gray-500 mt-1">{s.v}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.2 }}
          className="lg:col-span-5 relative"
        >
          <div className="relative rounded-[2rem] overflow-hidden border border-gray-200 shadow-2xl bg-white">
            <img
              src={heroImg}
              alt="PPrints studio work"
              className="w-full h-[400px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-brand-ink/40 via-transparent" />
            <div className="absolute bottom-5 left-5 right-5 flex items-center justify-between">
              <div className="glass rounded-2xl px-4 py-3 border border-white/40">
                <div className="text-[10px] uppercase tracking-widest text-gray-700 font-bold">Now Printing</div>
                <div className="font-display font-bold text-brand-ink">High-Volume Offset Run</div>
              </div>
              <div className="hidden sm:flex h-12 w-12 items-center justify-center rounded-full bg-brand-green text-white shadow-lg">
                <Sparkles className="h-5 w-5" />
              </div>
            </div>
          </div>

          <div className="absolute -bottom-6 -left-6 w-44 rounded-2xl bg-brand-blue text-white p-4 shadow-xl hidden md:block">
            <div className="text-xs uppercase tracking-widest text-white/70 font-bold">Avg. Delivery</div>
            <div className="font-display text-3xl font-extrabold mt-1">24–48 hrs</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
